import React, { useEffect, useState } from "react";
import "./../Styles/Admin.css";
import logoLight from "./../Images/tsqs-hm-ft-logo1.gif";
import axios from "axios";
import Lottie from "lottie-react";
import comingSoonAnimation from "./../ani/comingsoon.json";
import Notifications from "./../Components/Notifications";
import EmployeeDetailsDialog from "./EmployeeDetailsDialog";
import ProfileDialog from "./ProfileDialog";
import LeadManagement from "./LeadManagement";
import GoalAssessmentTable from "./GoalAssessmentTable";
import AppHeader from './AppHeader';
import Organization from './Organization';
import History from './History';
import EmployeeDirectoryTable from './EmployeeDirectoryTable';
import ReviewedReports from './Reports/ReviewedReports';

const apiUrl = process.env.REACT_APP_API_URL;

const Admin = () => {
  const [employees, setEmployees] = useState([]);
  const [managerInfo, setManagerInfo] = useState({
    full_name: "",
    job_title: "",
  });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [isMainManagerFlag, setIsMainManagerFlag] = useState(false);
  const [isProfileDialogOpen, setProfileDialogOpen] = useState(false);

  const VALID_ADMIN_SECTIONS = ['employees', 'assigned_goals', 'submitted', 'organization', 'leads', 'reports', 'projects', 'updates'];
  const [currentSection, setCurrentSection] = useState(() => {
    const savedSection = localStorage.getItem('admin_currentSection');
    return VALID_ADMIN_SECTIONS.includes(savedSection) ? savedSection : 'employees';
  });

  useEffect(() => {
    if (VALID_ADMIN_SECTIONS.includes(currentSection)) {
      localStorage.setItem('admin_currentSection', currentSection);
    }
  }, [currentSection]);

  const managerId = localStorage.getItem("employeeId");

  const fetchEmployees = async (mgrId, isMain) => {
    const idToUse = mgrId || managerId;
    const mainToUse = isMain !== undefined ? isMain : isMainManagerFlag;
    try {
      const response = await axios.get(`${apiUrl}/api/employees`, {
        params: {
          managerId: idToUse,
          isMainManager: mainToUse,
        },
      });
      setEmployees(response.data || []);
    } catch (error) {
      console.error("Error fetching employee data:", error);
    }
  };

  useEffect(() => {
    if (!managerId) {
      console.error("Manager ID not found in local storage.");
      return;
    }

    axios.get(`${apiUrl}/api/manager-info/${managerId}`)
      .then((response) => {
        setManagerInfo(response.data);
        const isMain = response.data.is_main_manager;
        setIsMainManagerFlag(isMain);
        fetchEmployees(managerId, isMain);
      })
      .catch((error) => console.error("Error fetching manager info:", error));
  }, [managerId]);

  useEffect(() => {
    if (currentSection === "employees") {
      fetchEmployees();
    }
  }, [currentSection]);

  const handleOpenProfileDialog = () => {
    setProfileDialogOpen(true);
  };

  const handleCloseProfileDialog = () => {
    setProfileDialogOpen(false);
  };

  return (
    <div className="admin-page">
      <aside className="sidebar">
        <div className="logo-container">
          <img src={logoLight} alt="Company Logo" className="logo" />
        </div>
        <nav className="nav-menu">
          <a
            href="#"
            className={`nav-item ${currentSection === "employees" ? "active" : ""}`}
            onClick={(e) => { e.preventDefault(); setCurrentSection("employees"); }}
          >
            Employees
          </a>
          <a
            href="#"
            className={`nav-item ${currentSection === "assigned_goals" ? "active" : ""}`}
            onClick={(e) => { e.preventDefault(); setCurrentSection("assigned_goals"); }}
          >
            Assigned Goals
          </a>
          <a
            href="#"
            className={`nav-item ${currentSection === "submitted" ? "active" : ""}`}
            onClick={(e) => { e.preventDefault(); setCurrentSection("submitted"); }}
          >
            Submitted
          </a>
          <a
            href="#"
            className={`nav-item ${currentSection === "organization" ? "active" : ""}`}
            onClick={(e) => { e.preventDefault(); setCurrentSection("organization"); }}
          >
            Organization
          </a>
          <a
            href="#"
            className={`nav-item ${currentSection === "leads" ? "active" : ""}`}
            onClick={(e) => { e.preventDefault(); setCurrentSection("leads"); }}
          >
            Lead Management
          </a>
          <a
            href="#"
            className={`nav-item ${currentSection === "reports" ? "active" : ""}`}
            onClick={(e) => { e.preventDefault(); setCurrentSection("reports"); }}
          >
            Reviewed Reports
          </a>
          <a
            href="#"
            className={`nav-item ${currentSection === "projects" ? "active" : ""}`}
            onClick={(e) => { e.preventDefault(); setCurrentSection("projects"); }}
          >
            Project Reports
          </a>
          <a
            href="#"
            className={`nav-item ${currentSection === "updates" ? "active" : ""}`}
            onClick={(e) => { e.preventDefault(); setCurrentSection("updates"); }}
          >
            Updates
          </a>
        </nav>
      </aside>

      <main className="main-content">
        <AppHeader
          title="MANAGER DASHBOARD"
          employeeInfo={managerInfo}
          onProfileClick={handleOpenProfileDialog}
          hideLogo={true}
        />

        {currentSection !== "organization" && currentSection !== "reports" && (
          <div className="welcome-section">
            <h1>Hi {managerInfo.full_name}...</h1>
          </div>
        )}

        {currentSection === "assigned_goals" && (
          <GoalAssessmentTable employeeId={managerId} />
        )}

        {currentSection === "submitted" && (
          <History embedded={true} />
        )}

        {currentSection === "employees" && (
          <EmployeeDirectoryTable
            title="List of Employees"
            employees={employees}
            onViewDetails={(emp) => setSelectedEmployee(emp)}
            showGoalActions={true}
            showProjectFilter={true}
            showRoleFilter={true}
            availableRoles={['all', 'lead', 'employee']}
            loggedInManagerId={managerId}
            isMainManagerFlag={isMainManagerFlag}
          />
        )}

        {currentSection === "leads" && (
          <div className="leads-section">
            <LeadManagement managerId={managerId} />
          </div>
        )}

        {currentSection === "reports" && (
          <div className="reports-section-wrapper">
            <ReviewedReports
              onViewDetails={(emp, quarter) => {
                setSelectedEmployee(emp);
                setSelectedQuarter(quarter);
              }}
            />
          </div>
        )}

        {currentSection === "projects" && (
          <div>
            Projects COMING SOON...
            <Lottie animationData={comingSoonAnimation} className="card-animation" />
          </div>
        )}

        {currentSection === "updates" && (
          <div className="updates-section">
            <Notifications managerId={managerId} isMainManager={Boolean(isMainManagerFlag)} />
          </div>
        )}

        {currentSection === "organization" && (
          <div className="organization-section-wrapper">
            <Organization 
              managerName={managerInfo.full_name ? managerInfo.full_name.split(' ')[0] : 'Admin'} 
              usersEndpoint={`${apiUrl}/api/employees?managerId=${managerId}&isMainManager=${isMainManagerFlag}`}
            />
          </div>
        )}
      </main>

      {selectedEmployee && (
        <EmployeeDetailsDialog
          employee={selectedEmployee}
          selectedQuarter={selectedQuarter}
          reviewerRole="admin"
          readOnly={currentSection === "reports" || Boolean(selectedQuarter)}
          onClose={() => {
            setSelectedEmployee(null);
            setSelectedQuarter(null);
          }}
        />
      )}

      {isProfileDialogOpen && (
        <ProfileDialog
          employeeInfo={managerInfo}
          onClose={handleCloseProfileDialog}
          onUpdate={(updatedData) => setManagerInfo(prev => ({ ...prev, ...updatedData }))}
        />
      )}
    </div>
  );
};

export default Admin;
